import { PRIVANA_LOCALES } from '../../shared/locales.mjs';
import { PORTAL_LOCALE_CODES } from './portalLocaleOptions.js';
import { useEffect, useRef } from 'react';
import { Globe2 } from 'lucide-react';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select.jsx';

const appearances = {
  members: {
    trigger: 'w-full max-w-none rounded-xl border-[var(--mp-panel-border)] bg-[var(--mp-panel-bg)] text-[var(--mp-text)] hover:bg-white/10',
    menu: 'rounded-xl border-[var(--mp-panel-border)] bg-slate-950',
  },
  terminal: {
    trigger: 'rounded-xl border-white/15 bg-neutral-900 hover:bg-neutral-800',
    menu: 'rounded-xl border-white/15 bg-neutral-900',
  },
  hotel: {
    trigger: 'rounded-full border-white/20 bg-white/10 backdrop-blur-md hover:bg-white/20',
    menu: 'rounded-2xl border-white/20 bg-neutral-950/95 backdrop-blur-md',
  },
  starter: {
    trigger: 'rounded-xl border-white/20 bg-white/5 font-medium hover:bg-white/10',
    menu: 'rounded-xl border-white/20 bg-[#061a36]',
  },
};
const stopPropagation = event => event.stopPropagation();

// Presentation only. Locale ownership, persistence and saving remain with callers.
export default function LanguageSelector({ appearance, value, onValueChange, disabled, inheritLabel, portal = false }) {
  const style = appearances[appearance];
  const options = (portal ? PORTAL_LOCALE_CODES : ['en-GB', 'es']).map(code => [code, code === 'en-GB' ? 'English' : PRIVANA_LOCALES[code].nativeName]);
  const selectedLabel = value === 'inherit' ? inheritLabel : options.find(([code]) => code === value)?.[1] || PRIVANA_LOCALES[value]?.nativeName || 'English';
  const triggerRef = useRef(null);
  const restoreAfterSave = useRef(false);
  const disabledRef = useRef(disabled);
  disabledRef.current = disabled;
  useEffect(() => {
    if (!disabled && restoreAfterSave.current) {
      restoreAfterSave.current = false;
      const fallback = triggerRef.current?.closest('[role="dialog"]') || document.body;
      if (document.activeElement === document.body || document.activeElement === fallback) {
        triggerRef.current?.focus({ preventScroll: true });
      }
    }
  }, [disabled]);
  return <Select value={value} onValueChange={onValueChange} disabled={disabled}>
    <SelectTrigger ref={triggerRef} aria-label={`Language / Idioma: ${selectedLabel}`} title={selectedLabel} data-language-appearance={appearance}
      onClick={stopPropagation} onKeyDown={stopPropagation}
      className={`h-11 min-h-11 w-auto min-w-0 max-w-[min(14rem,calc(100vw-2rem))] shrink-0 gap-2 px-3 text-sm text-white shadow-sm focus:ring-0 focus-visible:ring-2 focus-visible:ring-white/70 focus-visible:ring-offset-2 focus-visible:ring-offset-neutral-900 ${style.trigger}`}>
      <Globe2 aria-hidden="true" className="h-4 w-4 shrink-0 text-white/65" />
      <span className="min-w-0 truncate"><SelectValue /></span>
    </SelectTrigger>
    <SelectContent onCloseAutoFocus={event => {
      // Read current saving state even when the closing content retained its
      // earlier callback during the exit animation.
      event.preventDefault();
      if (disabledRef.current) restoreAfterSave.current = true;
      else triggerRef.current?.focus({ preventScroll: true });
    }} data-language-appearance={appearance} align="end" sideOffset={4} collisionPadding={12}
      onClick={stopPropagation} onPointerUp={stopPropagation} onKeyDown={stopPropagation}
      className={`max-h-[var(--radix-select-content-available-height)] max-w-[calc(100vw-1.5rem)] text-white shadow-xl shadow-black/30 data-[state=open]:animate-none data-[state=closed]:animate-none motion-reduce:transition-none ${style.menu}`}>
      {[...options, ...(inheritLabel ? [['inherit', inheritLabel]] : [])].map(([option, label]) =>
        <SelectItem key={option} value={option}
          className="min-h-11 rounded-lg py-2 pl-3 pr-9 text-white data-[state=checked]:bg-white/10 focus:bg-white/15 focus:text-white data-[highlighted]:bg-white/15 data-[highlighted]:text-white [&>span:last-child]:whitespace-normal [&>span:last-child]:break-words">
          {label}
        </SelectItem>)}
    </SelectContent>
  </Select>;
}
